import Head from "next/head";
import { Link } from "@mui/material";
import { TbShirtSport } from "react-icons/tb";
import { HiOutlineTicket } from "react-icons/hi";
import { MdOutlineEventSeat, MdOutlinedFlag } from "react-icons/md";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";
import React from "react";
import { useRouterExtended } from "@/global/hooks/useRouterExtended";

export const howItWorks = [
  {
    icon: <MdOutlinedFlag />,
    title: "Choose Your Games",
    description: "Select the games you wish to attend.",
  },
  {
    icon: <MdOutlineEventSeat />,
    title: "Login to Account Manager",
    description:
      "Click Continue and connect to your Milwaukee Bucks account using our secure Ticketmaster login.",
  },
  {
    icon: <TbShirtSport />,
    title: "Select Your Seats",
    description: "Select the seats you wish to purchase for each game.",
  },
  {
    icon: <HiOutlineTicket />,
    title: "Get Your Tickets",
    description: (
      <>
        Tickets will be available in your Milwaukee Bucks{" "}
        <Link
          target="_blank"
          href="https://am.ticketmaster.com/bucks/"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          Account Manager
        </Link>{" "}
        within 24 hours of purchase. Reach out to Live Fan Support for any
        questions.
      </>
    ),
  },
];

export const faqs = [
  {
    title: "How does the Bucks 2025-26 Flex Plan work?",
    description:
      "The Bucks 2025-26 Flex Plan lets you fully customize your experience. You choose any home games that best fit your schedule and select any available seats for each game — giving you flexibility and control over your season.",
  },
  {
    title: "What is 10% arena credit?",
    description:
      "Get 10% back on your total spend as in-arena credit to use on food and beverages throughout the season. Credit will be automatically loaded to your account in time for tipoff and is valid through the end of the 2025–26 regular season. You will be able to access your credit in the Bucks app by tapping the Member Benefits button in the Tickets tab. Credit is good at all Bucks Pro Shop locations in the arena as well concessions (except Just Walk Out).",
  },
  {
    title: "What is Lyft or SpotHero Credit?",
    description:
      "Customers will receive an email from the Bucks with redemption information for their Lyft or SpotHero credit. Email will be delivered within 2 business days of purchase.",
  },
  {
    title: "Do I have to select all my games now?",
    description: "Yes, you must select all games at the time of purchase.",
  },
  {
    title: "Can I choose seats throughout the venue?",
    description: "Yes, you can choose from all current available inventory.",
  },
  {
    title: "Where are my tickets delivered?",
    description: (
      <>
        Event tickets are delivered directly within your Milwaukee Bucks{" "}
        <Link
          target="_blank"
          href="https://am.ticketmaster.com/bucks/"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          Account Manager
        </Link>
        .
      </>
    ),
  },
  {
    title: "Would you like be to contacted by a Bucks Sales Representative?",
    description: (
      <>
        Please call{" "}
        <Link
          target="_blank"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="tel:4142270599"
        >
          (414) 227-0599
        </Link>{" "}
        or email{" "}
        <Link
          target="_blank"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
          href="mailto:tickets@bucks.com"
        >
          tickets@bucks.com
        </Link>{" "}
        to get connected with a representative.
      </>
    ),
  },
];

export const BucksCheckoutTerms = () => (
  <>
    By checking this box, you agree to the following{" "}
    <Link
      href="https://www.nba.com/bucks/terms-and-conditions"
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
    >
      Terms & Conditions
    </Link>
    ,{" "}
    <Link
      href="https://www.attend.tech/terms-of-use"
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
    >
      Terms of Use
    </Link>{" "}
    &{" "}
    <Link
      href="https://www.attend.tech/privacy-policy"
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
    >
      Privacy Policy
    </Link>
    .
  </>
);

export const BucksRegistrationTerms = () => (
  <>
    By checking this box you agree to receive notifications from the Milwaukee
    Bucks and agree to the following{" "}
    <Link
      href="https://www.nba.com/bucks/terms-and-conditions"
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
    >
      Terms & Conditions
    </Link>
    ,{" "}
    <Link
      href="https://www.attend.tech/terms-of-use"
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
    >
      Terms of Use
    </Link>{" "}
    &{" "}
    <Link
      href="https://www.attend.tech/privacy-policy"
      target={"_blank"}
      sx={{
        color: "primary.main",
        textDecoration: "none",
        fontWeight: 500,
      }}
    >
      Privacy Policy
    </Link>
    .
  </>
);

export const AnalyticsHeader = (props: { teamConfig: TeamConfig }) => {
  const router = useRouterExtended();
  const { teamSchema } = props.teamConfig;

  React.useEffect(() => {
    const handleRouteChange = (path: string) => {
      try {
        const url = `${window.location.origin}/${path}`;
        if (window?.Munchkin) {
          // firing custom page view event.
          window?.Munchkin?.munchkinFunction("visitWebPage", { url });
        }
      } catch (err) {
        console.log(err);
      }
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  return (
    <Head>
      <script
        nonce={process.env.munchkinScriptNonce}
        id={`${teamSchema}-munchkin-script`}
        defer
        dangerouslySetInnerHTML={{
          __html: `
              (function() {
                var didInit = false;
                function initMunchkin() {
                  if (didInit === false) {
                    didInit = true;
                    Munchkin.init('045-KRA-598');
                  }
                }
                var s = document.createElement('script');
                s.type = 'text/javascript';
                s.async = true;
                s.src = '//munchkin.marketo.net/munchkin.js';
                s.onreadystatechange = function() {
                  if (this.readyState == 'complete' || this.readyState == 'loaded') {
                    initMunchkin();
                  }
                };
                s.onload = initMunchkin;
                document.getElementsByTagName('head')[0].appendChild(s);
              })();
            `,
        }}
      />
    </Head>
  );
};
